import React, { FC } from 'react';
import Input from '../Input/Input';
import TextArea from '../TextArea/TextArea';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { Trans, useTranslation } from 'gatsby-plugin-react-i18next';
import { ANY_STRING } from '../../../constants/constants';
import Link from '../Link/Link';
import * as classes from './ContactForm.module.scss';
import { useDispatch } from 'react-redux';
import { sendFrom } from '../../../store/form/formActions';
import { TemplatesList } from '../../../enums/TemplatesList';
import ModernButton from '../../Templates/ModernTemplate/Components/ModernButton/ModernButton';
import DefaultButton from '../../Templates/DefaultTemplate/Components/DefaultButton/DefaultButton';

interface Props {
  professionalID: string;
  template: TemplatesList;
}

const ContactForm: FC<Props> = ({ template, professionalID }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const formik = useFormik({
    initialValues: {
      professionalId: professionalID,
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      message: '',
    },
    validationSchema: Yup.object({
      firstName: Yup.string()
        .required(t('getInTouch.formSection.form.errorMessages.required'))
        .max(50, t('getInTouch.formSection.form.errorMessages.max', { value: 50 })),
      lastName: Yup.string()
        .required(t('getInTouch.formSection.form.errorMessages.required'))
        .max(50, t('getInTouch.formSection.form.errorMessages.max', { value: 50 })),
      email: Yup.string()
        .email(t('getInTouch.formSection.form.errorMessages.email'))
        .required(t('getInTouch.formSection.form.errorMessages.required'))
        .max(50, t('getInTouch.formSection.form.errorMessages.max', { value: 50 })),
      phone: Yup.string().max(50, t('getInTouch.formSection.form.errorMessages.max', { value: 50 })),
      message: Yup.string()
        .required(t('getInTouch.formSection.form.errorMessages.required'))
        .max(2000, t('getInTouch.formSection.form.errorMessages.max', { value: 2000 })),
    }),
    onSubmit: (values, { resetForm }) => {
      dispatch(sendFrom(values, resetForm));
      return;
    },
  });

  return (
    <form onSubmit={formik.handleSubmit} className={classes.wrapper}>
      <div className={'row'}>
        <div className={'col-sm-6'}>
          <Input
            isReq
            type={'text'}
            label={t('getInTouch.formSection.form.firstName.label')}
            placeholder={t('getInTouch.formSection.form.firstName.placeholder')}
            id={'firstName'}
            name={'firstName'}
            onChange={formik.handleChange}
            value={formik.values.firstName}
            error={formik.errors.firstName}
            touched={formik.touched.firstName}
          />
        </div>
        <div className={'col-sm-6'}>
          <Input
            isReq
            type={'text'}
            label={t('getInTouch.formSection.form.lastName.label')}
            placeholder={t('getInTouch.formSection.form.lastName.placeholder')}
            id={'lastName'}
            name={'lastName'}
            onChange={formik.handleChange}
            value={formik.values.lastName}
            error={formik.errors.lastName}
            touched={formik.touched.lastName}
          />
        </div>
        <div className={'col-sm-6'}>
          <Input
            isReq
            type={'text'}
            label={t('getInTouch.formSection.form.email.label')}
            placeholder={t('getInTouch.formSection.form.email.placeholder')}
            id={'email'}
            name={'email'}
            onChange={formik.handleChange}
            value={formik.values.email}
            error={formik.errors.email}
            touched={formik.touched.email}
          />
        </div>
        <div className={'col-sm-6'}>
          <Input
            type={'text'}
            label={t('getInTouch.formSection.form.phone.label')}
            placeholder={t('getInTouch.formSection.form.phone.placeholder')}
            id={'phone'}
            name={'phone'}
            onChange={formik.handleChange}
            value={formik.values.phone}
            error={formik.errors.phone}
            touched={formik.touched.phone}
          />
        </div>
        <div className={'col-sm-12'}>
          <TextArea
            isReq
            label={t('getInTouch.formSection.form.message.label')}
            placeholder={t('getInTouch.formSection.form.message.placeholder')}
            id={'message'}
            name={'message'}
            onChange={formik.handleChange}
            value={formik.values.message}
            error={formik.errors.message}
            touched={formik.touched.message}
            className={classes.textArea}
          />
        </div>
        <div className={'col-sm-12'}>
          {/*TODO add animation*/}
          {template === TemplatesList.MODERN && <ModernButton type={'submit'}>{t('getInTouch.formSection.form.buttonText')}</ModernButton>}
          {template === TemplatesList.DEFAULT && (
            <DefaultButton type={'submit'}>{t('getInTouch.formSection.form.buttonText')}</DefaultButton>
          )}
          <div className={classes.underButtonText}>
            <Trans i18nKey={'getInTouch.formSection.form.underButtonText.text'}>
              {ANY_STRING}
              <Link path={t('getInTouch.formSection.form.underButtonText.link')} isExternalLink>
                {ANY_STRING}
              </Link>
            </Trans>
          </div>
        </div>
      </div>
    </form>
  );
};

export default ContactForm;
